import api from "../utils/axios";
import { getCached, invalidateCache } from "../utils/requestCache";
import { Configuration } from "../types/Configuration";
import { ZoneRaw } from "../types/sortingMachine";
import {
	CashRegister,
	ConfigurationId,
	CurrentUser,
	DocumentType,
	OrderStatus,
	SettlementStatus,
	UserRole,
	Vat,
} from "../types/Lookups";

/*
 * Reference data that many screens need and that rarely changes, served from the request cache
 * (utils/requestCache): one request per session per list instead of one per screen open. Entries
 * are kept per user (anonymous visitors included), survive a reload, and logging out clears them.
 *
 * Each piece of data is declared once below as a `Lookup` — its cache key, how long a copy stays
 * fresh, and how to fetch it — and read through one of:
 *
 * - `useLookup(lookup)` (hooks/useLookup) in components;
 * - `getLookup(lookup)` in async code;
 * - `invalidateLookup(lookup)` on the screen that edits the data, after a save.
 *
 * Only cache data that nothing in the app changes, or whose single editor screen invalidates it.
 * The user, client and staff lists are deliberately not here: they change during the day (new,
 * approved or blocked accounts) and a stale copy would hide someone from a dropdown.
 */

export interface Lookup<T> {
	key: string;
	ttlMs: number;
	fetch: () => Promise<T>;
	/** False for personal data: kept in memory only, never in sessionStorage. Default true. */
	persist?: boolean;
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

const getData = async <T>(
	url: string,
	config: { params?: object; silent?: boolean } = {}
): Promise<T> => {
	const response = await api.get<T>(url, config);
	if (response.status !== 200) {
		throw new Error(`GET ${url} returned ${response.status}`);
	}
	return response.data;
};

/** The data, from the cache while it is fresh. Rejects if the request fails. */
export const getLookup = <T>(lookup: Lookup<T>): Promise<T> =>
	getCached(lookup.key, lookup.ttlMs, lookup.fetch, {
		persist: lookup.persist,
	});

/** Drops the cached copy, so the next reader fetches it again. */
export const invalidateLookup = (lookup: Lookup<unknown>) =>
	invalidateCache(lookup.key);

/**
 * Fetches the data fresh, ignoring any cached copy, and stores the result for the other readers.
 * For screens that edit what they read: a form filled from a cached copy would save stale values.
 */
export const refreshLookup = <T>(lookup: Lookup<T>): Promise<T> => {
	invalidateLookup(lookup);
	return getLookup(lookup);
};

/* Lookups */

// Same key as the Dashboard's own zones resource, so they share one copy.
export const ZONES_CACHE_KEY = "reference:zones";

export const zonesLookup: Lookup<ZoneRaw[]> = {
	key: ZONES_CACHE_KEY,
	ttlMs: 30 * MINUTE,
	fetch: () => getData("Zone/zones"),
};

export const orderStatusesLookup: Lookup<OrderStatus[]> = {
	key: "reference:order-statuses",
	ttlMs: HOUR,
	fetch: () => getData("ListOptions/selectorderstatus"),
};

export const settlementStatusesLookup: Lookup<SettlementStatus[]> = {
	key: "reference:settlement-statuses",
	ttlMs: HOUR,
	fetch: () => getData("ListOptions/select-settlement-status"),
};

export const vatLookup: Lookup<Vat[]> = {
	key: "reference:vat",
	ttlMs: HOUR,
	fetch: () => getData("Currency/select-vat"),
};

export const documentTypesLookup: Lookup<DocumentType[]> = {
	key: "reference:document-types",
	ttlMs: HOUR,
	fetch: () => getData("ListOptions/document-types"),
};

/** Edited in Finance › Cash registers, which invalidates it after a save. */
export const cashRegistersLookup: Lookup<CashRegister[]> = {
	key: "reference:cash-registers",
	ttlMs: 30 * MINUTE,
	fetch: () => getData("ListOptions/cash-register"),
};

/** Edited in Arrangements › User roles, which invalidates it after a save. */
export const userRolesLookup: Lookup<UserRole[]> = {
	key: "reference:user-roles",
	ttlMs: HOUR,
	fetch: () => getData("ListOptions/select-user-roles"),
};

/**
 * The logged-in user's profile, for showing it (the Header, General). Personal data, so it is
 * kept in memory only. Screens that edit it (My account) read it fresh with `refreshLookup`.
 */
export const currentUserLookup: Lookup<CurrentUser> = {
	key: "profile:current-user",
	ttlMs: 10 * MINUTE,
	fetch: () => getData("User/userbyid", { silent: true }),
	persist: false,
};

/**
 * One row of the `Configurations` table. Only for values that no screen edits (see
 * `ConfigurationId`): a cached copy is kept for an hour.
 */
export const configurationLookup = (
	id: ConfigurationId
): Lookup<string | undefined> => ({
	key: `reference:configuration:${id}`,
	ttlMs: HOUR,
	fetch: () =>
		getData<Configuration[]>("ListOptions/select-configurations", {
			params: { id },
		}).then((rows) => rows[0]?.value),
});

/* Wrappers kept for callers written against the old fetchers: undefined instead of a rejection. */

const getLookupOrUndefined = async <T>(
	lookup: Lookup<T>
): Promise<T | undefined> => {
	try {
		return await getLookup(lookup);
	} catch (error) {
		console.error(`Failed to load ${lookup.key}:`, error);
		return undefined;
	}
};

/** The zones, or undefined if the request failed. */
export const fetchZonesCached = (): Promise<ZoneRaw[] | undefined> =>
	getLookupOrUndefined(zonesLookup);

/** Call after creating or editing a country, city or section. */
export const invalidateZones = () => invalidateLookup(zonesLookup);

/** The order statuses, or undefined if the request failed. */
export const fetchStatusCached = (): Promise<OrderStatus[] | undefined> =>
	getLookupOrUndefined(orderStatusesLookup);

/** One system setting, or undefined if the request failed. */
export const getConfigurationValueCached = (id: ConfigurationId) =>
	getLookupOrUndefined(configurationLookup(id));
